/**
 * SpeedCurve Metronome Application v2.7 (Progression System Complete)
 *
 * =====================================================================================
 * JOURNAL DES MODIFICATIONS (PROGRESSION SYSTEM)
 *
 * 1.  RESTAURATION COMPLÈTE DE L'UX DES INDICATEURS DE TEMPS (RÉGRESSION MAJEURE CORRIGÉE) :
 *     - Intégration de la logique de rendu visuel du code de référence dans `updateBeatIndicators`.
 *     - La fonction recrée désormais la structure DOM correcte avec les `<div class="beat-group">`,
 *       ce qui permet au CSS de les afficher en groupes (ex: 3+2) et de les rendre réactifs.
 *     - Cette opération n'a lieu que lors des changements de configuration par l'utilisateur,
 *       préservant ainsi les performances en temps réel.
 *
 * 2.  MAINTIEN DE TOUTES LES FONCTIONNALITÉS PRÉCÉDENTES :
 *     - La logique de pause/reprise, la gestion des états des boutons et la mise à l'échelle
 *       dynamique et correcte du graphe sont toutes conservées et fonctionnelles.
 *
 * 3.  ARCHITECTURE HYBRIDE STABILISÉE :
 *     - L'application combine désormais l'architecture de performance (worker, multi-canvas,
 *       API d'animation) avec la logique de rendu UI/UX éprouvée du code de référence,
 *       offrant le meilleur des deux mondes.
 *
 * 4.  CORRECTION DU BUG D'AFFICHAGE DU COCKPIT POUR LES TEMPS IRRÉGULIERS :
 *     - Le bug où les indicateurs de temps sortaient du cadre pour les signatures
 *       comme 11, 13, 17, etc., a été corrigé. Cela a été réalisé en améliorant
 *       la logique de `generateTimeSignaturePattern` dans le `MetronomeEngine`
 *       (et son équivalent dans le worker). L'algorithme décompose désormais
 *       ces temps irréguliers en combinaisons standard de groupes de 2 et 3
 *       (par exemple, 11/4 sera affiché comme 2,2,2,2,3 ou 3,3,3,2),
 *       permettant au CSS Flexbox de les envelopper correctement.
 *
 * 5.  EXPLICATION DE LA THÉORIE RYTHMIQUE :
 *     - Une nouvelle section a été ajoutée dans la modale d'information (`info-modal`)
 *       expliquant pourquoi les groupements par 2 et 3 sont fondamentaux en théorie
 *       rythmique (groupements binaires, ternaires et asymétriques).
 *
 * 6.  FONCTIONNALITÉ DE GROUPEMENT RYTHMIQUE PERSONNALISÉ :
 *     - Une nouvelle option "Groupement Rythmique Personnalisé" a été ajoutée
 *       dans la section des réglages avancés.
 *     - L'utilisateur peut désormais saisir une séquence de nombres séparés par
 *       des virgules (par exemple, "2,3,2" pour un 7/4) pour définir manuellement
 *       la structure de groupement des temps.
 *     - Le système valide la somme des groupes par rapport au nombre total de temps
 *       par mesure et fournit un feedback visuel.
 *     - Le groupement personnalisé est enregistré et rechargé pour chaque signature
 *       de temps spécifique.
 *     - L'affichage des indicateurs de temps et des accents s'adapte dynamiquement
 *       à ce groupement personnalisé.
 *
 * 7.  CORRECTION DE LA RÉGRESSION DES INDICATEURS VISUELS PENDANT LA LECTURE :
 *     - La fonction `resyncPlaybackIfRunning` a été refactorisée pour toujours
 *       forcer un redémarrage complet du moteur du métronome (stop puis start)
 *       lors d'un changement de signature de temps ou de groupement personnalisé.
 *       Ceci garantit que la timeline du worker est systématiquement régénérée
 *       avec la configuration la plus à jour, résolvant le problème de désynchronisation
 *       et de non-affichage des indicateurs visuels et du tempo.
 *
 * 8.  RÉSOLUTION DU BUG DES ANIMATIONS MANQUANTES DES INDICATEURS VISUELS (V2 - DÉFINITIVE) :
 *     - **Cause :** Désynchronisation temporelle entre la régénération du DOM des indicateurs
 *       de temps et le redémarrage du worker. Le worker pouvait envoyer des événements `beat`
 *       avant que le DOM ne soit prêt, ou la référence `lastActiveIndicator` était périmée.
 *       Une incohérence dans les versions précédentes de code a annulé la correction.
 *     - **Correction (ré-implémentée et confirmée) :**
 *         1.  Inversion de l'ordre d'appel dans `handleTimeSignatureChange` : la mise à jour
 *             de l'interface utilisateur (y compris la recréation des indicateurs DOM) est
 *             maintenant effectuée *avant* la resynchronisation de la lecture du métronome.
 *             Ceci est CRUCIAL et a été re-vérifié.
 *         2.  Réinitialisation explicite de `this.lastActiveIndicator` à `null` dans la fonction
 *             `updateBeatIndicators` après avoir vidé son `innerHTML`, et dans `clearBeatIndicators`.
 *             Cela garantit que la référence est toujours à jour après la recréation des éléments DOM,
 *             permettant aux animations de s'appliquer correctement.
 *         3.  Amélioration de la robustesse de `updateBeatIndicatorDisplay` pour vérifier si `lastActiveIndicator`
 *             est toujours attaché au DOM avant de tenter de le manipuler.
 *
 * 9.  IMPLÉMENTATION DE LA FONCTIONNALITÉ DE FEEDBACK UTILISATEUR :
 *     - Ajout d'un bouton "Feedback" (icône de bulle de chat) dans le header.
 *     - Création d'une pop-up modale avec un formulaire de feedback (suggestion ou bug).
 *     - Support pour l'upload d'images (limité à 10 Mo total par envoi) via `FormData`.
 *     - Validation des champs (type de feedback, longueur de la description, taille des fichiers).
 *     - Intégration avec Formspree pour l'envoi d'e-mails à une adresse spécifiée.
 *     - Messages de succès/erreur conviviaux pour guider l'utilisateur.
 *     - Internationalisation complète des nouveaux éléments.
 * =====================================================================================
 */
import { MetronomeEngine } from './metronome-engine.js';
import { I18n } from './i18n.js';
import { db } from './db.js';
import './styles.css';

class SpeedCurveMetronomeApp {
    constructor() {
        this.metronome = new MetronomeEngine();
        this.i18n = new I18n();
        this.elements = {};
        this.cachedDOMElements = {
            accentButtons: [],
        };
        this.lastFocusedElement = null;
        this.isAudioInitialized = false;
        this.currentTheme = 'dark';
        this.MAX_BEATS = 32;

        this.audioContext = null;
        this.audioBuffers = {};
        this.failedSounds = [];

        this.graphData = [];
        this.customTempoPoints = [120];
        this.accentMemory = {};
        this.customGroupingMemory = {}; // NEW: To store custom groupings per time signature
        this.needsGraphRedraw = { background: true, curve: true };
        this.uiState = {};
        this.rhythmicState = {};
        this.lastActiveIndicator = null; // This is the element that was last active

        this.uiUpdateScheduled = false;
        this.pendingRafId = null;
        this.latestBeatData = null;
        this.latestProgress = 0;
        this.throttledGraphHover = this.throttle(this.handleGraphHover.bind(this), 50);
        this.debouncedRedraw = this.debounce(() => {
            this.needsGraphRedraw = { background: true, curve: true };
            this.drawGraph();
        }, 250);
        this.infoModalContent = null;
        this.infoModalLang = null; // Track language for info modal cache
        this.graphColors = {};

        // Stats modal cache
        this.cachedHeatmapHtml = null;
        this.statsModalCacheKey = null;

        // i18n translation cache for hot paths
        this.cachedTranslations = {};
        this.cacheStaticTranslations();

        this.presets = {};
        this.loadPresetsFromStorage();

        // Session tracking
        this.db = db;
        this.currentSession = null;
        this.sessionStartTime = null;

        this.setDefaultRhythmicState();
        this.setDefaultUiState();

        console.log('SpeedCurve Metronome App v2.7 Initialized (Presets & Auto-Save Ready)');
    }

    throttle(func, limit) {
        let inThrottle;
        return function () {
            const args = arguments;
            const context = this;
            if (!inThrottle) {
                func.apply(context, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    }

    debounce(func, delay) {
        let timeout;
        return function () {
            const context = this;
            const args = arguments;
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(context, args), delay);
        };
    }

    // ============================================================
    // PRESET MANAGEMENT
    // ============================================================

    loadPresetsFromStorage() {
        try {
            const saved = localStorage.getItem('speedcurve_presets');
            if (saved) {
                this.presets = JSON.parse(saved);
            }
        } catch (e) {
            console.warn('Failed to load presets:', e);
            this.presets = {};
        }
    }

    savePresetsToStorage() {
        try {
            localStorage.setItem('speedcurve_presets', JSON.stringify(this.presets));
        } catch (e) {
            console.warn('Failed to save presets:', e);
        }
    }

    getCurrentSettings() {
        return {
            trainingProgram: this.elements.trainingProgram?.value || 'pyramid',
            targetBpm: parseInt(this.elements.targetBpm?.value) || 120,
            startBpm: parseInt(this.elements.startBpm?.value) || 80,
            beatsPerMeasure: parseInt(this.elements.beatsPerMeasure?.value) || 4,
            noteValue: parseInt(this.elements.noteValue?.value) || 4,
            measuresPerStep: parseInt(this.elements.measuresPerStep?.value) || 1,
            soundType: this.elements.soundType?.value || 'classic',
            volume: parseInt(this.elements.volume?.value) || 75,
            subdivisionMode: this.elements.subdivisionMode?.value || 'none',
            swingRatio: parseInt(this.elements.swingRatio?.value) || 66,
            visualFlash: this.elements.visualFlash?.checked || false,
            accentLevel: parseFloat(this.elements.accentLevel?.value) || 1.5,
            customAccents: [...this.rhythmicState.customAccents],
            customGroupingPattern: this.rhythmicState.customGroupingPattern ? [...this.rhythmicState.customGroupingPattern] : null,
            customTempoPoints: [...this.customTempoPoints],
            theme: this.currentTheme,
        };
    }

    loadSettings(settings) {
        if (!settings) return;

        if (settings.trainingProgram) this.elements.trainingProgram.value = settings.trainingProgram;
        if (settings.targetBpm) this.elements.targetBpm.value = settings.targetBpm;
        if (settings.startBpm) this.elements.startBpm.value = settings.startBpm;
        if (settings.beatsPerMeasure) this.elements.beatsPerMeasure.value = settings.beatsPerMeasure;
        if (settings.noteValue) this.elements.noteValue.value = settings.noteValue;
        if (settings.measuresPerStep) this.elements.measuresPerStep.value = settings.measuresPerStep;
        if (settings.soundType) this.elements.soundType.value = settings.soundType;
        if (settings.volume) this.elements.volume.value = settings.volume;
        if (settings.subdivisionMode) this.elements.subdivisionMode.value = settings.subdivisionMode;
        if (settings.swingRatio) this.elements.swingRatio.value = settings.swingRatio;
        if (settings.visualFlash !== undefined) this.elements.visualFlash.checked = settings.visualFlash;
        if (settings.accentLevel) this.elements.accentLevel.value = settings.accentLevel;
        if (settings.customAccents) this.rhythmicState.customAccents = [...settings.customAccents];
        if (settings.customGroupingPattern) this.rhythmicState.customGroupingPattern = [...settings.customGroupingPattern];
        if (settings.customTempoPoints) this.customTempoPoints = [...settings.customTempoPoints];
        if (settings.theme) this.currentTheme = settings.theme;
    }

    savePreset(name) {
        if (!name || name.trim() === '') {
            this.showDialog(this.i18n.translate('preset_name_required') || 'Please enter a preset name', this.i18n.translate('error_title'), true);
            return false;
        }
        this.presets[name.trim()] = this.getCurrentSettings();
        this.savePresetsToStorage();
        this.showPresetsModal();
        return true;
    }

    loadPreset(name) {
        if (!this.presets[name]) return;
        this.stop();
        this.loadSettings(this.presets[name]);

        // Sync UI and metronome
        this.generateProgram();
        this.updateMetronomeProgram();
        this.updateMetronomeConfig();
        this.updateTimeSignatureUI();
        this.updateCustomGroupingUI();
        this.updateProgramDescription();
        this.updateCustomTempoDisplay();
        this.updateStaticTempoDisplay();
        this.needsGraphRedraw = { background: true, curve: true };
        this.drawGraph();
        this.updateControlsState();

        this.showDialog(`Preset "${name}" loaded`, this.i18n.translate('preset_loaded') || 'Preset Loaded', false);
    }

    deletePreset(name) {
        if (!this.presets[name]) return;
        delete this.presets[name];
        this.savePresetsToStorage();
        this.showPresetsModal();
    }

    // ============================================================
    // AUTO-SAVE STATE
    // ============================================================

    saveCurrentStateToLocalStorage() {
        try {
            const state = {
                settings: this.getCurrentSettings(),
                accentMemory: this.accentMemory,
                customGroupingMemory: this.customGroupingMemory,
            };
            localStorage.setItem('speedcurve_autosave', JSON.stringify(state));
        } catch (e) {
            console.warn('Failed to auto-save state:', e);
        }
    }

    loadStateFromLocalStorage() {
        try {
            const saved = localStorage.getItem('speedcurve_autosave');
            if (!saved) return false;

            const state = JSON.parse(saved);

            if (state.settings) {
                this.loadSettings(state.settings);
            }
            if (state.accentMemory) {
                this.accentMemory = { ...this.accentMemory, ...state.accentMemory };
            }
            if (state.customGroupingMemory) {
                this.customGroupingMemory = { ...this.customGroupingMemory, ...state.customGroupingMemory };
            }
            return true;
        } catch (e) {
            console.warn('Failed to load auto-saved state:', e);
            return false;
        }
    }

    clearAutoSavedState() {
        localStorage.removeItem('speedcurve_autosave');
    }

    setupAutoSave() {
        // Auto-save on any input/change events
        const controls = ['trainingProgram', 'targetBpm', 'startBpm', 'beatsPerMeasure',
            'noteValue', 'measuresPerStep', 'soundType', 'volume',
            'subdivisionMode', 'swingRatio', 'visualFlash', 'accentLevel'];

        controls.forEach(control => {
            const el = this.elements[this.toCamelCase(control.replace(/([A-Z])/g, '-$1').toLowerCase())];
            if (el) {
                el.addEventListener('change', () => this.saveCurrentStateToLocalStorage());
                el.addEventListener('input', () => this.debounceAutoSave());
            }
        });

        // Also save when custom tempo points change (handled by their own listeners)
        const originalUpdateCustomTempoDisplay = this.updateCustomTempoDisplay.bind(this);
        this.updateCustomTempoDisplay = (...args) => {
            originalUpdateCustomTempoDisplay(...args);
            this.saveCurrentStateToLocalStorage();
        };
    }

    debounceAutoSave() {
        clearTimeout(this.autoSaveTimeout);
        this.autoSaveTimeout = setTimeout(() => this.saveCurrentStateToLocalStorage(), 500);
    }

    // ============================================================
    // SESSION TRACKING & STATS
    // ============================================================

    async updateStatsAfterSession() {
        try {
            const sessions = await this.db.getAllSessions();
            const now = new Date();
            const todayStr = now.toISOString().split('T')[0];

            if (sessions.length === 0) {
                await this.db.saveStats({
                    totalSessions: 0,
                    totalPracticeMinutes: 0,
                    longestStreak: 0,
                    currentStreak: 0,
                    lastPracticeDate: null,
                    programUsage: {},
                    maxTempoReached: 0,
                    level: 1,
                    xp: 0,
                    xpToNextLevel: 100
                });
                return;
            }

            // Sort sessions by timestamp descending (newest first)
            const sortedSessions = sessions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

            // Calculate longest streak (can span any time period)
            let longestStreak = 0;
            let tempStreak = 0;
            let lastDate = null;

            for (const session of sortedSessions) {
                const sessionDate = session.timestamp.split('T')[0];
                if (!lastDate) {
                    tempStreak = 1;
                    lastDate = sessionDate;
                } else {
                    const diff = (new Date(lastDate) - new Date(sessionDate)) / (1000 * 60 * 60 * 24);
                    if (diff <= 1.5) {
                        tempStreak++;
                        lastDate = sessionDate;
                    } else {
                        if (tempStreak > longestStreak) longestStreak = tempStreak;
                        tempStreak = 1;
                        lastDate = sessionDate;
                    }
                }
            }
            if (tempStreak > longestStreak) longestStreak = tempStreak;

            // Calculate current streak - must have a session today or yesterday to have an active streak
            let currentStreak = 0;
            const uniqueDays = [...new Set(sessions.map(s => s.timestamp.split('T')[0]))].sort().reverse();

            if (uniqueDays.length > 0) {
                const mostRecentDay = uniqueDays[0];
                const daysSinceLast = (new Date(todayStr) - new Date(mostRecentDay)) / (1000 * 60 * 60 * 24);

                // Only set currentStreak if most recent session is within 1.5 days
                if (daysSinceLast <= 1.5) {
                    // Count consecutive days from most recent
                    currentStreak = 1;
                    for (let i = 1; i < uniqueDays.length; i++) {
                        const prevDay = new Date(uniqueDays[i-1]);
                        const currDay = new Date(uniqueDays[i]);
                        const diff = (prevDay - currDay) / (1000 * 60 * 60 * 24);
                        if (diff <= 1.5) {
                            currentStreak++;
                        } else {
                            break;
                        }
                    }
                }
            }

            // Calculate total practice time
            const totalMinutes = sessions.reduce((sum, s) => sum + (s.duration_seconds || 0), 0) / 60;

            // Count program usage
            const programUsage = {};
            sessions.forEach(s => {
                programUsage[s.program] = (programUsage[s.program] || 0) + 1;
            });

            // Find max BPM
            const maxTempo = Math.max(...sessions.map(s => s.achievedBpm || 0), 0);

            // Calculate XP (simplified)
            const baseXP = 25;
            const minutesXP = Math.floor(totalMinutes * 2);
            const streakXP = Math.min(currentStreak * 10, 100);
            const totalXP = baseXP + minutesXP + streakXP;

            // Calculate level (each level needs level * 150 XP)
            let level = 1;
            let xpNeeded = 100;
            let tempXP = totalXP;
            while (tempXP >= xpNeeded) {
                tempXP -= xpNeeded;
                level++;
                xpNeeded = level * 150;
            }

            const stats = {
                totalSessions: sessions.length,
                totalPracticeMinutes: Math.floor(totalMinutes),
                longestStreak,
                currentStreak,
                lastPracticeDate: sessions[0]?.timestamp || null,
                programUsage,
                maxTempoReached: maxTempo,
                level,
                xp: tempXP,
                xpToNextLevel: xpNeeded
            };

            await this.db.saveStats(stats);

            // Check achievements
            this.checkAchievements(sessions, stats);
        } catch (e) {
            console.warn('Failed to update stats:', e);
        }
    }

    async checkAchievements(sessions, stats) {
        const achievements = await this.db.getAllAchievements();
        const achieved = new Set(achievements.map(a => a.id));

        // Define achievements to check
        const toCheck = [
            { id: 'first_session', check: () => stats.totalSessions >= 1 },
            { id: 'century', check: () => stats.totalSessions >= 100 },
            { id: 'pyramid_10', check: () => (stats.programUsage?.pyramid || 0) >= 10 },
            { id: 'endurance_10', check: () => (stats.programUsage?.endurance || 0) >= 10 },
            { id: 'ladder_20', check: () => (stats.programUsage?.ladder || 0) >= 20 },
            { id: 'speed_200', check: () => stats.maxTempoReached >= 200 },
            { id: 'marathon', check: () => sessions.some(s => (s.duration_seconds || 0) >= 3000) },
            { id: 'consistency_7', check: () => stats.longestStreak >= 7 },
            { id: 'comeback', check: () => stats.currentStreak >= 1 && stats.totalSessions >= 2 },
        ];

        for (const achievement of toCheck) {
            if (!achieved.has(achievement.id) && achievement.check()) {
                await this.db.saveAchievement({
                    id: achievement.id,
                    unlockedAt: new Date().toISOString(),
                    progress: 100
                });
                this.showAchievementToast(achievement.id);
            }
        }

        // Explorer achievement (try all 7 programs)
        const allPrograms = ['pyramid', 'consolidation', 'endurance', 'speed', 'agility', 'ladder', 'custom'];
        const usedPrograms = Object.keys(stats.programUsage || {});
        if (!achieved.has('explorer') && allPrograms.every(p => usedPrograms.includes(p))) {
            await this.db.saveAchievement({ id: 'explorer', unlockedAt: new Date().toISOString(), progress: 100 });
            this.showAchievementToast('explorer');
        }
    }

    showAchievementToast(achievementId) {
        const achievementData = {
            first_session: { name: this.i18n.translate('achievement_first_session'), icon: '🎯', desc: this.i18n.translate('achievement_first_session_desc') },
            century: { name: this.i18n.translate('achievement_century'), icon: '💯', desc: this.i18n.translate('achievement_century_desc') },
            pyramid_10: { name: this.i18n.translate('achievement_pyramid_10'), icon: '🔺', desc: this.i18n.translate('achievement_pyramid_10_desc') },
            endurance_10: { name: this.i18n.translate('achievement_endurance_10'), icon: '🏃', desc: this.i18n.translate('achievement_endurance_10_desc') },
            ladder_20: { name: this.i18n.translate('achievement_ladder_20'), icon: '🪜', desc: this.i18n.translate('achievement_ladder_20_desc') },
            speed_200: { name: this.i18n.translate('achievement_speed_200'), icon: '⚡', desc: this.i18n.translate('achievement_speed_200_desc') },
            marathon: { name: this.i18n.translate('achievement_marathon'), icon: '🏅', desc: this.i18n.translate('achievement_marathon_desc') },
            consistency_7: { name: this.i18n.translate('achievement_consistency_7'), icon: '🔥', desc: this.i18n.translate('achievement_consistency_7_desc') },
            comeback: { name: this.i18n.translate('achievement_comeback'), icon: '🔄', desc: this.i18n.translate('achievement_comeback_desc') },
            explorer: { name: this.i18n.translate('achievement_explorer'), icon: '🗺️', desc: this.i18n.translate('achievement_explorer_desc') },
        };

        const ach = achievementData[achievementId];
        if (!ach) return;

        // Create toast notification
        const toast = document.createElement('div');
        toast.className = 'achievement-toast';
        toast.innerHTML = `
            <span class="toast-icon">${ach.icon}</span>
            <span class="toast-text"><strong>${this.i18n.translate('achievement_unlocked')}</strong><br>${ach.name}</span>
        `;

        // Apply styles
        toast.style.cssText = `
            position: fixed;
            bottom: 100px;
            left: 50%;
            transform: translateX(-50%);
            background: var(--surface-bg);
            border: 1px solid var(--accent-primary);
            border-radius: var(--border-radius-lg);
            padding: var(--spacing-md) var(--spacing-xl);
            display: flex;
            align-items: center;
            gap: var(--spacing-md);
            z-index: 9999;
            animation: toastSlideIn 0.3s ease-out;
            box-shadow: 0 4px 20px rgba(0,0,0,0.3);
        `;

        document.body.appendChild(toast);
        setTimeout(() => {
            toast.style.animation = 'toastSlideOut 0.3s ease-in';
            setTimeout(() => toast.remove(), 300);
        }, 4000);
    }

    // ============================================================
    // PRESETS MODAL
    // ============================================================

    showPresetsModal() {
        const presetNames = Object.keys(this.presets);
        const presetsHtml = presetNames.length === 0
            ? `<p class="no-presets">${this.i18n.translate('no_presets') || 'No saved presets'}</p>`
            : `<div class="presets-list">${presetNames.map(name => `
                <div class="preset-item">
                    <span class="preset-name">${name}</span>
                    <div class="preset-actions">
                        <button class="preset-btn load" data-preset="${name}">${this.i18n.translate('load') || 'Load'}</button>
                        <button class="preset-btn delete" data-preset="${name}">${this.i18n.translate('delete') || 'Delete'}</button>
                    </div>
                </div>
            `).join('')}</div>`;

        const modalContent = `
            <div class="presets-modal-content">
                <div class="preset-save-form">
                    <input type="text" id="preset-name-input" class="control-input" placeholder="${this.i18n.translate('preset_name_placeholder') || 'Preset name'}" />
                    <button class="control-btn primary" id="save-preset-btn">${this.i18n.translate('save_preset') || 'Save Current'}</button>
                </div>
                <div class="presets-divider"></div>
                <h4>${this.i18n.translate('saved_presets') || 'Saved Presets'}</h4>
                ${presetsHtml}
            </div>
        `;

        this.showModal(this.i18n.translate('presets_title') || 'Presets', modalContent);

        // Attach event listeners
        const saveBtn = document.getElementById('save-preset-btn');
        const nameInput = document.getElementById('preset-name-input');
        if (saveBtn && nameInput) {
            saveBtn.onclick = () => {
                const name = nameInput.value.trim();
                if (name && this.savePreset(name)) {
                    // Modal will be refreshed by savePreset
                }
            };
            nameInput.onkeydown = (e) => {
                if (e.key === 'Enter') saveBtn.click();
            };
        }

        // Attach load/delete handlers
        document.querySelectorAll('.preset-btn.load').forEach(btn => {
            btn.onclick = () => {
                const name = btn.dataset.preset;
                this.hidePresetsModal();
                this.loadPreset(name);
            };
        });
        document.querySelectorAll('.preset-btn.delete').forEach(btn => {
            btn.onclick = () => {
                const name = btn.dataset.preset;
                if (confirm(this.i18n.translate('confirm_delete_preset') || `Delete preset "${name}"?`)) {
                    this.deletePreset(name);
                }
            };
        });
    }

    showChangelogModal() {
        const t = (key) => this.i18n.translate(key);
        const changelogContent = `
            <div class="changelog-content">
                <div class="changelog-entry">
                    <div class="changelog-date">2026-05-29</div>
                    <div class="changelog-version">${t('changelog_v27_title')}</div>
                    <ul class="changelog-list">
                        <li>${t('changelog_v27_desc1')}</li>
                        <li>${t('changelog_v27_desc2')}</li>
                        <li>${t('changelog_v27_desc3')}</li>
                    </ul>
                </div>
                <div class="changelog-entry">
                    <div class="changelog-date">2026-05-28</div>
                    <div class="changelog-version">${t('changelog_v26_title')}</div>
                    <ul class="changelog-list">
                        <li>${t('changelog_v26_desc1')}</li>
                        <li>${t('changelog_v26_desc2')}</li>
                        <li>${t('changelog_v26_desc3')}</li>
                    </ul>
                </div>
                <div class="changelog-entry">
                    <div class="changelog-date">2026-05-27</div>
                    <div class="changelog-version">${t('changelog_v25_title')}</div>
                    <ul class="changelog-list">
                        <li>${t('changelog_v25_desc1')}</li>
                        <li>${t('changelog_v25_desc2')}</li>
                    </ul>
                </div>
            </div>
        `;
        this.showModal(t('changelog_title'), changelogContent);
    }

    async showStatsModal() {
        const stats = await this.db.getStats() || {};
        const sessions = await this.db.getAllSessions() || [];
        const achievements = await this.db.getAllAchievements() || [];

        // Check cache - invalidate when session count changes
        const cacheKey = sessions.length;
        if (this.cachedHeatmapHtml && this.statsModalCacheKey === cacheKey) {
            // Use cached heatmap HTML - still build the rest of the modal dynamically
            const statsContent = this.buildStatsContent(stats, sessions, achievements, this.cachedHeatmapHtml);
            this.showModal(this.i18n.translate('stats_title') || 'Statistics', statsContent);
            return;
        }

        // Generate heatmap data for last 12 weeks
        const today = new Date();
        const weeks = 12;
        const daysPerWeek = 7;
        const heatmapData = [];

        for (let w = weeks - 1; w >= 0; w--) {
            const weekData = [];
            for (let d = 0; d < daysPerWeek; d++) {
                const date = new Date(today);
                date.setDate(date.getDate() - (w * 7 + d));
                const dateStr = date.toISOString().split('T')[0];
                const daySessions = sessions.filter(s => s.timestamp.split('T')[0] === dateStr);
                const minutes = daySessions.reduce((sum, s) => sum + (s.duration_seconds || 0), 0) / 60;
                weekData.push({ date: dateStr, minutes, count: daySessions.length });
            }
            heatmapData.push(weekData);
        }

        // Build heatmap HTML
        const dayLabels = this.getCachedTranslation('heatmap_day_labels');
        let heatmapHtml = '<div class="heatmap-grid">';
        heatmapHtml += '<div class="heatmap-day-labels">' + dayLabels.map(d => `<span>${d}</span>`).join('') + '</div>';
        heatmapHtml += '<div class="heatmap-weeks">';
        for (const week of heatmapData) {
            heatmapHtml += '<div class="heatmap-week">';
            for (const day of week) {
                const intensity = day.minutes > 0 ? Math.min(Math.floor(day.minutes / 10) + 1, 4) : 0;
                const title = `${day.date}: ${Math.round(day.minutes)} min`;
                heatmapHtml += `<div class="heatmap-cell intensity-${intensity}" title="${title}"></div>`;
            }
            heatmapHtml += '</div>';
        }
        heatmapHtml += '</div></div>';

        // Cache the heatmap HTML
        this.cachedHeatmapHtml = heatmapHtml;
        this.statsModalCacheKey = cacheKey;

        // Build stats content with cached heatmap
        const statsContent = this.buildStatsContent(stats, sessions, achievements, heatmapHtml);
        this.showModal(this.i18n.translate('stats_title') || 'Statistics', statsContent);

        // Attach export/import handlers
        setTimeout(() => {
            const exportBtn = document.getElementById('export-data-btn');
            const importBtn = document.getElementById('import-data-btn');
            if (exportBtn) exportBtn.onclick = () => this.exportAllData();
            if (importBtn) importBtn.onclick = () => this.triggerImport();
        }, 100);
    }

    buildStatsContent(stats, sessions, achievements, heatmapHtml) {
        // Build achievements list
        const achievementList = [
            { id: 'first_session', name: this.i18n.translate('achievement_first_session'), desc: this.i18n.translate('achievement_first_session_desc'), icon: '🎯' },
            { id: 'century', name: this.i18n.translate('achievement_century'), desc: this.i18n.translate('achievement_century_desc'), icon: '💯' },
            { id: 'pyramid_10', name: this.i18n.translate('achievement_pyramid_10'), desc: this.i18n.translate('achievement_pyramid_10_desc'), icon: '🔺' },
            { id: 'endurance_10', name: this.i18n.translate('achievement_endurance_10'), desc: this.i18n.translate('achievement_endurance_10_desc'), icon: '🏃' },
            { id: 'ladder_20', name: this.i18n.translate('achievement_ladder_20'), desc: this.i18n.translate('achievement_ladder_20_desc'), icon: '🪜' },
            { id: 'speed_200', name: this.i18n.translate('achievement_speed_200'), desc: this.i18n.translate('achievement_speed_200_desc'), icon: '⚡' },
            { id: 'consistency_7', name: this.i18n.translate('achievement_consistency_7'), desc: this.i18n.translate('achievement_consistency_7_desc'), icon: '🔥' },
            { id: 'marathon', name: this.i18n.translate('achievement_marathon'), desc: this.i18n.translate('achievement_marathon_desc'), icon: '🏅' },
            { id: 'comeback', name: this.i18n.translate('achievement_comeback'), desc: this.i18n.translate('achievement_comeback_desc'), icon: '🔄' },
            { id: 'explorer', name: this.i18n.translate('achievement_explorer'), desc: this.i18n.translate('achievement_explorer_desc'), icon: '🗺️' },
        ];

        const unlockedIds = new Set(achievements.map(a => a.id));
        let achievementsHtml = '<div class="achievements-grid">';
        for (const ach of achievementList) {
            const unlocked = unlockedIds.has(ach.id);
            achievementsHtml += `<div class="achievement-item ${unlocked ? 'unlocked' : 'locked'}">
                <span class="achievement-icon">${unlocked ? ach.icon : '🔒'}</span>
                <span class="achievement-name">${ach.name}</span>
                <span class="achievement-desc">${ach.desc}</span>
            </div>`;
        }
        achievementsHtml += '</div>';

        // Build stats content
        return `
            <div class="stats-dashboard">
                <div class="stats-summary">
                    <div class="stat-card">
                        <span class="stat-value">${stats.totalSessions || 0}</span>
                        <span class="stat-label">${this.i18n.translate('stats_total_sessions')}</span>
                    </div>
                    <div class="stat-card">
                        <span class="stat-value">${stats.totalPracticeMinutes || 0}</span>
                        <span class="stat-label">${this.i18n.translate('stats_total_time')}</span>
                    </div>
                    <div class="stat-card">
                        <span class="stat-value">${stats.currentStreak || 0}</span>
                        <span class="stat-label">${this.i18n.translate('stats_current_streak')}</span>
                    </div>
                    <div class="stat-card">
                        <span class="stat-value">${stats.maxTempoReached || 0}</span>
                        <span class="stat-label">${this.i18n.translate('stats_max_tempo')}</span>
                    </div>
                </div>

                <div class="stats-level">
                    <span class="level-label">${this.i18n.translate('stats_level')} ${stats.level || 1}</span>
                    <div class="xp-bar">
                        <div class="xp-fill" style="width: ${((stats.xp || 0) / (stats.xpToNextLevel || 100)) * 100}%"></div>
                    </div>
                    <span class="xp-text">${stats.xp || 0} / ${stats.xpToNextLevel || 100} ${this.i18n.translate('stats_xp')}</span>
                </div>

                <h4>${this.i18n.translate('stats_dashboard')}</h4>
                ${heatmapHtml}

                <h4>${this.i18n.translate('achievements_title')}</h4>
                ${achievementsHtml}

                <div class="export-import-controls">
                    <button class="control-btn" id="export-data-btn">${this.i18n.translate('export_btn_label')}</button>
                    <button class="control-btn" id="import-data-btn">${this.i18n.translate('import_btn_label')}</button>
                </div>
            </div>
        `;
    }

    async exportAllData() {
        try {
            const exportData = await this.db.exportAll();
            const json = JSON.stringify(exportData, null, 2);
            const blob = new Blob([json], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `speedcurve-backup-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Export failed:', e);
            this.showDialog(this.i18n.translate('export_error') || 'Export failed', '', true);
        }
    }

    triggerImport() {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            try {
                const text = await file.text();
                const data = JSON.parse(text);

                // Confirm import
                const confirmed = confirm(this.i18n.translate('import_confirm') || 'Replace existing data?');
                if (confirmed) {
                    await this.db.importAll(data, 'replace');
                    this.showDialog(this.i18n.translate('import_success') || 'Import successful', '', false);
                }
            } catch (e) {
                console.error('Import failed:', e);
                this.showDialog(this.i18n.translate('import_error') || 'Import failed', '', true);
            }
        };
        input.click();
    }

    showModal(title, content) {
        const modal = this.elements.infoModal;
        const titleEl = this.elements.infoModalTitle || modal.querySelector('.modal-title');
        const bodyEl = this.elements.infoModalBody || modal.querySelector('.modal-body');

        if (titleEl) titleEl.textContent = title;
        if (bodyEl) bodyEl.innerHTML = content;

        modal.classList.add('show');
        modal.classList.remove('active');
        this.lastFocusedElement = document.activeElement;
    }

    hidePresetsModal() {
        const modal = this.elements.infoModal;
        if (modal) modal.classList.remove('show');
        if (this.lastFocusedElement) this.lastFocusedElement.focus();
    }

    hideInfoModal() {
        const modal = this.elements.infoModal;
        if (modal) modal.classList.remove('show');
        if (this.lastFocusedElement) this.lastFocusedElement.focus();
    }

    setDefaultRhythmicState() {
        this.rhythmicState = {
            currentTimeSignature: { beats: 4, noteValue: 4 },
            customAccents: [0],
            subdivisionMode: 'none',
            customGroupingPattern: null, // NEW: Default to null
        };
    }

    setDefaultUiState() {
        this.uiState = {
            isPlaying: false,
            isPaused: false,
            isComplete: false,
            currentStep: 0,
            currentMeasure: 1,
            progress: 0,
        };
    }

    async initialize() {
        try {
            this.showLoadingScreen();
            await this.i18n.initialize();
            await this.db.open(); // Initialize IndexedDB
            this.infoModalLang = this.i18n.getCurrentLanguage();
            this.initializeElements();
            this.preallocateDOMElements();
            this.loadTheme();
            this.cacheGraphColors();
            this.setupEventListeners();
            this.setupMetronomeCallbacks();

            // Load auto-saved state or defaults, then update UI
            if (!this.loadStateFromLocalStorage()) {
                this.setDefaultRhythmicState();
                this.setDefaultUiState();
            }

            this.updateMetronomeConfig();
            this.generateProgram();
            this.updateMetronomeProgram();
            this.updateTranslations();
            this.updateProgramDescription();
            this.updateCustomTempoDisplay();
            this.updateTimeSignatureUI();
            this.updateCustomGroupingUI();
            this.updateControlsState();
            this.updateStatusText();
            this.updateStaticTempoDisplay();
            this.drawGraph();
            this.updateGraphIndicator(-1);
            this.hideLoadingScreen();

            // Set up auto-save on every config change
            this.setupAutoSave();
        } catch (error) {
            console.error('UI initialization error:', error);
            this.showDialog(`UI initialization error: ${error.message}`, this.i18n.translate('error_title'), true);
        }
    }

    initializeElements() {
        const elementIds = [
            'training-program', 'target-bpm', 'start-bpm',
            'beats-per-measure', 'note-value', 'measures-per-step', 'sound-type',
            'volume', 'swing-ratio', 'subdivision-mode', 'visual-flash',
            'start-btn', 'stop-btn', 'reset-btn', 'pause-btn',
            'current-tempo', 'step-info', 'beat-indicators', 'metronome-circle',
            'progress-fill', 'progress-text',
            'program-description', 'graph-tooltip', 'theme-toggle', 'language-toggle',
            'current-language', 'loading-screen', 'dialog-modal', 'dialog-title',
            'dialog-message', 'dialog-close', 'custom-tempo-points',
            'add-tempo-point', 'visual-flash-overlay', 'accent-buttons',
            'reset-accents', 'accent-level', 'start-bpm-container',
            'info-btn', 'info-modal', 'info-modal-close', 'info-modal-body', 'info-modal-title',
            'toggle-advanced', 'advanced-controls-container',
            'graph-background-canvas', 'graph-curve-canvas',
            'graph-indicator-line', 'graph-active-point',
            // Custom Grouping elements
            'custom-grouping-container', 'custom-grouping',
            // --- Feedback Modal Elements ---
            'feedback-btn', 'feedback-modal', 'feedback-modal-close',
            'feedback-form', 'feedback-type-suggestion', 'feedback-type-bug', 'feedback-description',
            'feedback-submit-btn', 'feedback-form-message', 'feedback-modal-title',
            // --- Presets Modal Elements ---
            'presets-btn',
            'changelog-btn',
            'stats-btn'
        ];
        elementIds.forEach(id => {
            const element = document.getElementById(id);
            if (element) {
                this.elements[this.toCamelCase(id)] = element;
            }
        });
        this.elements.volumeDisplay = document.querySelector('.volume-display');
        this.elements.swingDisplay = document.querySelector('.swing-display');
        this.elements.accentLevelDisplay = document.querySelector('.accent-level-display');
        this.elements.graphContainer = document.querySelector('.graph-container');
        this.elements.controlsPanel = document.querySelector('.controls-panel');
        // for custom grouping info text
        this.elements.customGroupingInfoText = document.querySelector('.custom-grouping-info');
        this.backgroundCanvas = this.elements.graphBackgroundCanvas;
        this.curveCanvas = this.elements.graphCurveCanvas;
        if (this.backgroundCanvas) this.backgroundContext = this.backgroundCanvas.getContext('2d');
        if (this.curveCanvas) this.curveContext = this.curveCanvas.getContext('2d');
    }

    preallocateDOMElements() {
        const accentContainer = this.elements.accentButtons;
        if (!accentContainer) return;
        for (let i = 0; i < this.MAX_BEATS; i++) {
            const button = document.createElement('button');
            button.className = 'accent-btn';
            button.textContent = i + 1;
            button.setAttribute('data-beat', i);
            button.style.display = 'none';
            const handler = () => this.toggleAccent(i);
            button._accentHandler = handler;
            button.addEventListener('click', handler);
            accentContainer.appendChild(button);
            this.cachedDOMElements.accentButtons.push(button);
        }
    }

    cleanupAccentButtons() {
        if (this.cachedDOMElements?.accentButtons) {
            for (const button of this.cachedDOMElements.accentButtons) {
                if (button._accentHandler) {
                    button.removeEventListener('click', button._accentHandler);
                    button._accentHandler = null;
                }
            }
            this.cachedDOMElements.accentButtons = [];
        }
    }

    async initializeAudio() {
        if (this.isAudioInitialized) return true;
        this.showLoadingScreen();
        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'interactive', sampleRate: 44100 });
            const { AUDIO_SAMPLES } = await import('./audio-mapping.js');
            const soundPromises = [];
            for (const [soundType, variants] of Object.entries(AUDIO_SAMPLES)) {
                this.audioBuffers[soundType] = {};
                for (const [variant, filePath] of Object.entries(variants)) {
                    const promise = (async () => {
                        try {
                            const response = await fetch(filePath);
                            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                            const arrayBuffer = await response.arrayBuffer();
                            this.audioBuffers[soundType][variant] = await this.audioContext.decodeAudioData(arrayBuffer);
                        } catch (error) { this.failedSounds.push(`${soundType}-${variant}`); }
                    })();
                    soundPromises.push(promise);
                }
            }
            await Promise.all(soundPromises);
            await this.metronome.initializeAudio(this.audioContext, this.audioBuffers);
            this.isAudioInitialized = true;
            return true;
        } catch (error) {
            console.error('Audio engine error:', error);
            this.showDialog(`Audio engine error: ${error.message}`, this.i18n.translate('error_title'), true);
            return false;
        } finally {
            this.hideLoadingScreen();
        }
    }

    setupEventListeners() {
        const addListener = (element, event, handler) => { if (element) element.addEventListener(event, handler); };
        const addValidatedListener = (element, event, handler) => {
            if (!element) return;
            // The handler for input changes was incorrect.
            // It should call the handler with `false` for resync, to avoid restarting the metronome on every digit typed.
            // The 'change' event on blur is the correct place to trigger a potential resync.
            const changeHandler = () => handler(true); // 'change' event implies a final value, so resync if needed.
            const inputHandler = () => handler(false); // 'input' event is for intermediate changes, no resync.

            if (element.type === 'number') {
                // On 'input', update config silently without restarting playback
                element.addEventListener('input', () => { 
                    if (parseFloat(element.value) > parseFloat(element.max)) element.value = element.max; 
                    inputHandler(); 
                });
                // On 'blur' (losing focus), validate and trigger the full handler with resync
                element.addEventListener('blur', () => {
                    let value = parseFloat(element.value);
                    const min = parseFloat(element.min) || 0;
                    const max = parseFloat(element.max) || 300;
                    if (isNaN(value) || value < min) element.value = min;
                    else if (value > max) element.value = max;
                    else element.value = Math.round(value);
                    changeHandler();
                });
            } else if (element.type === 'range') {
                 // For range sliders, 'input' is the primary event. We decide inside the handler if it needs resync.
                 // For volume, no resync. For swing, yes.
                addListener(element, 'input', () => handler(element.id === 'swing-ratio'));
            } 
            else { 
                // For selects and other inputs, 'change' is the correct event.
                addListener(element, 'change', () => handler(true));
            }
        };

        // Pass the handler functions directly. The logic to resync or not is now inside addValidatedListener or the handler itself.
        addValidatedListener(this.elements.trainingProgram, 'change', () => this.handleProgramChange());
        addValidatedListener(this.elements.targetBpm, 'change', () => this.handleProgramChange());
        addValidatedListener(this.elements.startBpm, 'change', () => this.handleProgramChange());
        addValidatedListener(this.elements.measuresPerStep, 'change', () => this.handleConfigChange(true));
        addValidatedListener(this.elements.beatsPerMeasure, 'change', () => this.handleTimeSignatureChange());
        addValidatedListener(this.elements.noteValue, 'change', () => this.handleTimeSignatureChange());
        addValidatedListener(this.elements.subdivisionMode, 'change', () => this.handleSubdivisionModeChange());
        addValidatedListener(this.elements.soundType, 'change', () => this.handleConfigChange(false));
        
        // Use a wrapper for range inputs to handle config changes
        const rangeHandler = (shouldResync) => this.handleConfigChange(shouldResync);
        addValidatedListener(this.elements.volume, 'input', () => rangeHandler(false)); // volume change shouldn't resync
        addValidatedListener(this.elements.swingRatio, 'input', () => rangeHandler(true)); // swing change must resync
        addValidatedListener(this.elements.accentLevel, 'input', () => rangeHandler(false)); // accent level change shouldn't resync immediately

        addListener(this.elements.resetAccents, 'click', () => this.resetAccents());
        addListener(this.elements.addTempoPoint, 'click', () => this.addCustomTempoPoint());
        addListener(this.elements.startBtn, 'click', () => this.start());
        addListener(this.elements.pauseBtn, 'click', () => this.togglePause());
        addListener(this.elements.stopBtn, 'click', () => this.stop());
        addListener(this.elements.resetBtn, 'click', () => this.reset());
        addListener(this.elements.themeToggle, 'click', () => this.toggleTheme());
        addListener(this.elements.languageToggle, 'click', () => this.toggleLanguage());
        addListener(this.elements.dialogClose, 'click', () => this.hideDialog());
        addListener(this.elements.graphContainer, 'mousemove', e => this.throttledGraphHover(e));
        addListener(this.elements.graphContainer, 'mouseleave', () => this.hideGraphTooltip());
        addListener(this.elements.graphContainer, 'click', e => this.handleGraphClick(e));
        addListener(this.elements.infoBtn, 'click', () => this.showInfoModal());
        addListener(this.elements.infoModalClose, 'click', () => this.hideInfoModal());
        addListener(this.elements.infoModal, 'click', e => { if (e.target === this.elements.infoModal) this.hideInfoModal(); });
        addListener(this.elements.toggleAdvanced, 'click', () => this.toggleAdvancedControls());
        if (this.elements.graphContainer) { new ResizeObserver(this.debouncedRedraw).observe(this.elements.graphContainer); }
        document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden' && this.uiState.isPlaying && !this.uiState.isPaused) this.pause(); });
        document.addEventListener('keydown', e => {
            if (['INPUT', 'SELECT', 'TEXTAREA'].includes(e.target.tagName)) return;
            if (e.code === 'Space') { e.preventDefault(); this.togglePlayback(); }
            if (e.code === 'Escape' && (this.uiState.isPlaying || this.uiState.isComplete)) { e.preventDefault(); this.stop(); }
        });

        // Custom Grouping Event Listener
        if (this.elements.customGrouping) {
            addListener(this.elements.customGrouping, 'input', () => this.handleCustomGroupingChange());
            addListener(this.elements.customGrouping, 'blur', () => this.handleCustomGroupingChange());
        }

        // Event delegation for dynamically created custom tempo points (avoids listener leaks)
        if (this.elements.customTempoPoints) {
            this.elements.customTempoPoints.addEventListener('change', e => {
                if (e.target.classList.contains('tempo-point-input')) {
                    const index = parseInt(e.target.dataset.index);
                    const value = parseInt(e.target.value);
                    this.customTempoPoints[index] = Math.max(20, Math.min(400, value || 120));
                    this.handleProgramChange();
                }
            });
            this.elements.customTempoPoints.addEventListener('click', e => {
                if (e.target.classList.contains('tempo-point-remove')) {
                    this.removeCustomTempoPoint(parseInt(e.target.dataset.index));
                }
            });
        }

        // --- Feedback Modal Listeners ---
        addListener(this.elements.feedbackBtn, 'click', () => this.showFeedbackModal());
        addListener(this.elements.feedbackModalClose, 'click', () => this.hideFeedbackModal());
        addListener(this.elements.feedbackModal, 'click', e => { if (e.target === this.elements.feedbackModal) this.hideFeedbackModal(); });
        addListener(this.elements.feedbackForm, 'submit', e => this.handleFeedbackSubmit(e));

        // --- Presets Modal Listeners ---
        addListener(this.elements.presetsBtn, 'click', () => this.showPresetsModal());
        addListener(this.elements.changelogBtn, 'click', () => this.showChangelogModal());

        // --- Stats Modal Listener ---
        addListener(this.elements.statsBtn, 'click', () => this.showStatsModal());
    }

    setupMetronomeCallbacks() { this.metronome.setCallbacks({ onBeat: (beatData, progress) => { this.latestBeatData = beatData; this.latestProgress = progress; this.scheduleUiUpdate(); }, onStepChange: step => { this.uiState.currentStep = step; this.updateGraphIndicator(step); this.updateStatusText(); }, onMeasureChange: measure => { this.uiState.currentMeasure = measure; this.updateStatusText(); }, onProgramComplete: () => { this.uiState.isComplete = true; this.stop(); this.showDialog(this.i18n.translate('program_complete_message'), this.i18n.translate('program_complete_title'), false); }, }); }
    
    handleProgramChange() {
        const programType = this.elements.trainingProgram.value;
        const customControls = this.elements.customTempoPoints.closest('.custom-only');
        if (customControls) {
            customControls.style.display = programType === 'custom' ? 'block' : 'none';
        }
    
        this.generateProgram();
        this.updateMetronomeProgram();
        this.resyncPlaybackIfRunning();
        this.updateProgramDescription();
        this.updateCustomTempoDisplay();
        this.updateStaticTempoDisplay();
        this.needsGraphRedraw = { background: true, curve: true };
        this.drawGraph();
    }

    handleConfigChange(shouldResync = false) { this.updateMetronomeConfig(); if (shouldResync) this.resyncPlaybackIfRunning(); }

    // BUGFIX (Race Condition): Inverted order of operations here to prevent visual regressions.
    // The UI must be updated *before* the metronome engine is resynced.
    handleTimeSignatureChange() {
        const sigKey = `${this.rhythmicState.currentTimeSignature.beats}/${this.rhythmicState.currentTimeSignature.noteValue}`;

        // Save current custom accents and grouping
        this.accentMemory[sigKey] = [...this.rhythmicState.customAccents];
        if (this.rhythmicState.customGroupingPattern) {
            this.customGroupingMemory[sigKey] = [...this.rhythmicState.customGroupingPattern];
        } else {
            delete this.customGroupingMemory[sigKey]; // Clear if it was reset or not custom
        }

        const beats = parseInt(this.elements.beatsPerMeasure.value) || 4;
        const noteValue = parseInt(this.elements.noteValue.value) || 4;
        this.rhythmicState.currentTimeSignature = { beats, noteValue };

        const newSigKey = `${beats}/${noteValue}`;

        // Load custom accents for new time signature
        if (this.accentMemory[newSigKey]) {
            this.rhythmicState.customAccents = this.accentMemory[newSigKey];
        } else {
            // Default accents based on analysis for new signature (without custom grouping initially)
            const timeInfo = this.metronome.analyzeTimeSignature(beats, noteValue, null);
            this.rhythmicState.customAccents = [...timeInfo.strongBeats];
        }

        // Load custom grouping for new time signature
        if (this.customGroupingMemory[newSigKey]) {
            this.rhythmicState.customGroupingPattern = this.customGroupingMemory[newSigKey];
        } else {
            this.rhythmicState.customGroupingPattern = null; // Clear if no custom saved
        }
        // Update custom grouping UI input field
        this.elements.customGrouping.value = this.rhythmicState.customGroupingPattern ? this.rhythmicState.customGroupingPattern.join(',') : '';

        // *** CRITICAL ORDER CHANGE FOR VISUAL REGRESSION FIX ***
        // Update UI elements (including re-rendering beat indicators DOM) FIRST.
        this.updateTimeSignatureUI();
        this.updateCustomGroupingUI();

        // THEN update metronome config and resync playback.
        // This ensures the DOM is ready BEFORE the worker starts sending beat messages
        // that need to interact with the newly rendered elements.
        this.updateMetronomeConfig();
        this.resyncPlaybackIfRunning();
    }
    
    handleSubdivisionModeChange() {
        this.rhythmicState.subdivisionMode = this.elements.subdivisionMode.value;
        this.updateMetronomeConfig();
        this.resyncPlaybackIfRunning();

        // Also fix visibility of swing controls, which was a persistent bug
        const swingControls = document.querySelector('.swing-controls');
        if (swingControls) {
            swingControls.style.display = this.elements.subdivisionMode.value === 'swing' ? 'block' : 'none';
        }
    }

    handleCustomGroupingChange() {
        const inputStr = this.elements.customGrouping.value.trim();
        const beatsPerMeasure = parseInt(this.elements.beatsPerMeasure.value);

        if (inputStr === '') {
            this.rhythmicState.customGroupingPattern = null;
            this.elements.customGrouping.classList.remove('is-invalid');
            if (this.elements.customGroupingInfoText) this.elements.customGroupingInfoText.textContent = this.i18n.translate('custom_grouping_info');
        } else {
            const parts = inputStr.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n) && n > 0);
            const sum = parts.reduce((acc, val) => acc + val, 0);

            if (sum === beatsPerMeasure && parts.length > 0) {
                this.rhythmicState.customGroupingPattern = parts;
                this.elements.customGrouping.classList.remove('is-invalid');
                if (this.elements.customGroupingInfoText) this.elements.customGroupingInfoText.textContent = this.i18n.translate('custom_grouping_info_valid');
            } else {
                this.rhythmicState.customGroupingPattern = null; // Invalidate custom grouping
                this.elements.customGrouping.classList.add('is-invalid');
                if (this.elements.customGroupingInfoText) {
                    const errorKey = (sum !== beatsPerMeasure) ? 'custom_grouping_error_sum' : 'custom_grouping_error_format';
                    this.elements.customGroupingInfoText.textContent = this.i18n.translate(errorKey, { beatsPerMeasure: beatsPerMeasure, sum: sum });
                }
            }
        }

        // Save custom grouping to memory for current time signature
        const sigKey = `${this.rhythmicState.currentTimeSignature.beats}/${this.rhythmicState.currentTimeSignature.noteValue}`;
        if (this.rhythmicState.customGroupingPattern) {
            this.customGroupingMemory[sigKey] = [...this.rhythmicState.customGroupingPattern];
        } else {
            delete this.customGroupingMemory[sigKey];
        }

        this.updateMetronomeConfig();
        this.resyncPlaybackIfRunning();
        this.updateTimeSignatureUI(); // This will redraw beat indicators based on new grouping
    }

    updateMetronomeConfig() {
        // Pass customGroupingPattern to the engine
        this.metronome.configure({
            beatsPerMeasure: parseInt(this.elements.beatsPerMeasure.value) || 4,
            noteValue: parseInt(this.elements.noteValue.value) || 4,
            measuresPerStep: parseInt(this.elements.measuresPerStep.value) || 1,
            soundType: this.elements.soundType.value,
            volume: parseFloat(this.elements.volume.value) / 100,
            subdivisionMode: this.elements.subdivisionMode.value,
            swingRatio: parseFloat(this.elements.swingRatio.value) / 100,
            customAccents: [...this.rhythmicState.customAccents],
            accentLevel: parseFloat(this.elements.accentLevel.value),
            customGroupingPattern: this.rhythmicState.customGroupingPattern, // NEW: pass custom grouping
        });
        // Update UI displays that depend on these values
        if(this.elements.volumeDisplay) this.elements.volumeDisplay.textContent = this.elements.volume.value;
        if(this.elements.swingDisplay) this.elements.swingDisplay.textContent = this.elements.swingRatio.value + '%';
        if(this.elements.accentLevelDisplay) this.elements.accentLevelDisplay.textContent = 'x' + parseFloat(this.elements.accentLevel.value).toFixed(1);
    }
    updateMetronomeProgram() { this.metronome.setProgram(this.graphData); }
    generateProgram() {
        const programType = this.elements.trainingProgram.value;
        const options = {
            startBpm: parseInt(this.elements.startBpm.value) || 80,
            targetBpm: parseInt(this.elements.targetBpm.value) || 120,
            totalSteps: 16,
            customPoints: [...this.customTempoPoints],
        };
        this.graphData = MetronomeEngine.generateProgram(programType, options);
    }

    // This function now correctly checks if the metronome is playing before trying to resync.
    // This prevents the metronome from starting unexpectedly when changing settings while it's stopped.
    async resyncPlaybackIfRunning() {
        // If the metronome isn't running at all, do nothing related to playback.
        // The configuration has already been updated by the calling function.
        if (!this.uiState.isPlaying) {
            return;
        }

        const wasPaused = this.uiState.isPaused;

        // Stop the metronome to clear its internal timeline. This is necessary to apply
        // changes that affect timing, like time signature or swing.
        this.metronome.stop();
        // Give the worker a moment to process the 'stop' command.
        await new Promise(resolve => setTimeout(resolve, 20));

        // The program (tempo steps) and config objects are already updated on the main thread.
        // We just need to ensure the engine's program data is current before restarting.
        this.updateMetronomeProgram();
        
        // Restart the metronome. This is correct because we are inside the isPlaying block.
        // The start command will make the worker regenerate its timeline with the latest config.
        this.metronome.start();

        // If the metronome was paused before the resync, put it back into the paused state.
        if (wasPaused) {
            // Calling this.pause() ensures the UI state and engine state are correctly managed.
            this.pause();
        }
    }

    async start() {
        if (this.uiState.isPlaying) return;
        if (!this.isAudioInitialized) { if (!(await this.initializeAudio())) return; }
        if (this.audioContext.state === 'suspended') { await this.audioContext.resume(); }
        this.uiState.isComplete = false;
        this.uiState.isPlaying = true;
        this.uiState.isPaused = false;

        // Start session tracking
        this.sessionStartTime = Date.now();
        this.currentSession = {
            id: crypto.randomUUID(),
            timestamp: new Date().toISOString(),
            program: this.elements.trainingProgram?.value || 'pyramid',
            startBpm: parseInt(this.elements.startBpm?.value) || 80,
            targetBpm: parseInt(this.elements.targetBpm?.value) || 120,
            timeSignature: `${this.rhythmicState.currentTimeSignature.beats}/${this.rhythmicState.currentTimeSignature.noteValue}`,
            stepsCompleted: 0,
            stepsTotal: this.metronome.programSteps?.length || 0,
            wasCompleted: false
        };

        this.updateMetronomeProgram();
        this.metronome.start();
        this.updateControlsState();
        this.updateStatusText();
    }
    async pause() { if (!this.uiState.isPlaying || this.uiState.isPaused) return; this.uiState.isPaused = true; await this.metronome.pause(); this.updateControlsState(); this.updateStatusText(); }
    async resume() { if (!this.uiState.isPaused) return; if (this.audioContext.state === 'suspended') { await this.audioContext.resume(); } this.uiState.isPaused = false; this.metronome.resume(); this.updateControlsState(); this.updateStatusText(); }
    togglePause() { this.uiState.isPaused ? this.resume() : this.pause(); }
    stop() {
        if (!this.uiState.isPlaying && !this.uiState.isComplete) return;

        // Cancel any pending RAF callback
        if (this.pendingRafId) {
            cancelAnimationFrame(this.pendingRafId);
            this.pendingRafId = null;
        }
        this.uiUpdateScheduled = false;

        // Guard against double-stop race condition
        if (!this.currentSession || !this.sessionStartTime) {
            // Already stopped or no active session
            this.metronome.stop();
            this.setDefaultUiState();
            this.updateControlsState();
            this.clearBeatIndicators();
            this.updateGraphIndicator(-1);
            return;
        }

        // Capture session data
        const durationSeconds = Math.floor((Date.now() - this.sessionStartTime) / 1000);
        const completedStep = this.uiState.currentStep || 0;

        // Calculate achieved BPM - use the step that was reached (not -1) and fallback to startBpm
        const achievedBpm = this.metronome.programSteps?.[completedStep] || this.currentSession.startBpm;

        // Clear session state BEFORE async operations to allow new sessions
        const sessionToSave = { ...this.currentSession };
        sessionToSave.duration_seconds = durationSeconds;
        sessionToSave.stepsCompleted = completedStep;
        sessionToSave.wasCompleted = this.uiState.isComplete || false;
        sessionToSave.achievedBpm = achievedBpm;

        this.currentSession = null;
        this.sessionStartTime = null;

        // Save to IndexedDB (fire and forget - but log errors)
        this.db.addSession(sessionToSave).catch(e => console.error('Failed to save session:', e));

        // Update stats
        this.updateStatsAfterSession();

        this.metronome.stop();
        this.setDefaultUiState();
        this.updateControlsState();
        this.updateStatusText();
        this.clearBeatIndicators();
        this.updateGraphIndicator(-1);
        this.updateStaticTempoDisplay();
    }
    togglePlayback() { if (!this.uiState.isPlaying) { this.start(); } else { this.togglePause(); } }
    reset() { if (!this.uiState.isPlaying || !this.uiState.isPaused) return; this.metronome.resetStep(); }
    toggleAccent(beatIndex) { const isAccented = this.rhythmicState.customAccents.includes(beatIndex); if (isAccented) { this.rhythmicState.customAccents = this.rhythmicState.customAccents.filter(b => b !== beatIndex); } else { this.rhythmicState.customAccents.push(beatIndex); } if (this.rhythmicState.customAccents.length === 0) { this.rhythmicState.customAccents = [0]; } this.updateMetronomeConfig(); this.resyncPlaybackIfRunning(); this.updateAccentButtons(); }
    resetAccents() {
        // Pass current custom grouping to ensure analyzeTimeSignature uses the correct grouping info for default strong beats
        const timeInfo = this.metronome.analyzeTimeSignature(
            this.rhythmicState.currentTimeSignature.beats,
            this.rhythmicState.currentTimeSignature.noteValue,
            this.rhythmicState.customGroupingPattern // NEW: Pass custom grouping here
        );
        this.rhythmicState.customAccents = [...timeInfo.strongBeats];
        this.updateMetronomeConfig();
        this.resyncPlaybackIfRunning();
        this.updateAccentButtons();
    }
    
    scheduleUiUpdate() {
        if (!this.uiUpdateScheduled) {
            this.uiUpdateScheduled = true;
            this.pendingRafId = requestAnimationFrame(() => {
                this.updateUIOnBeat(this.latestBeatData, this.latestProgress);
                this.uiUpdateScheduled = false;
                this.pendingRafId = null;
            });
        }
    }

    updateUIOnBeat(beatData, progress) {
        if (!beatData) return;
        this.uiState.progress = progress;
        if (this.elements.progressFill) {
            this.elements.progressFill.style.width = `${progress}%`;
        }
        this.updateBeatIndicatorDisplay(beatData);
        this.updateMetronomeDisplay(beatData);
        if (this.elements.visualFlash.checked) {
            this.triggerVisualFlash(beatData);
        }
        if (this.elements.currentTempo) {
            this.elements.currentTempo.textContent = beatData.bpm;
        }
    }

    updateControlsState() {
        const { isPlaying, isPaused, isComplete } = this.uiState;

        // Manage enabled/disabled states
        if (this.elements.controlsPanel) this.elements.controlsPanel.classList.toggle('disabled', isPlaying);
        if (this.elements.startBtn) this.elements.startBtn.disabled = isPlaying;
        if (this.elements.pauseBtn) this.elements.pauseBtn.disabled = !isPlaying;
        if (this.elements.stopBtn) this.elements.stopBtn.disabled = !isPlaying && !isComplete;
        if (this.elements.resetBtn) this.elements.resetBtn.disabled = !(isPlaying && isPaused);

        // Manage button colors and text
        if (!this.elements.pauseBtn) return;
        const pauseIcon = this.elements.pauseBtn.querySelector('.btn-icon');
        const pauseText = this.elements.pauseBtn.querySelector('span:not(.btn-icon)');
        if (isPaused) {
            if (pauseIcon) pauseIcon.textContent = '▶';
            if (pauseText) { pauseText.textContent = this.i18n.translate('resume'); pauseText.setAttribute('data-i18n', 'resume'); }
        } else {
            if (pauseIcon) pauseIcon.textContent = '⏸';
            if (pauseText) { pauseText.textContent = this.i18n.translate('pause'); pauseText.setAttribute('data-i18n', 'pause'); }
        }

        // Toggle color classes
        this.elements.stopBtn.classList.toggle('secondary', isPlaying || isComplete);
        this.elements.pauseBtn.classList.toggle('tertiary', isPlaying && !isPaused);
        this.elements.pauseBtn.classList.toggle('primary', isPlaying && isPaused); // Use primary color for "Resume"
    }

    updateStatusText() { const { isPlaying, isPaused, isComplete, currentStep, currentMeasure } = this.uiState; let text; if (isComplete) text = this.getCachedTranslation('program_complete_title'); else if (isPaused) text = this.getCachedTranslation('paused'); else if (isPlaying) { const stepText = `${this.getCachedTranslation('step')} ${currentStep + 1}/${this.graphData.length || 1}`; const measureText = `${this.getCachedTranslation('measure')} ${currentMeasure}/${parseInt(this.elements.measuresPerStep.value) || 1}`; text = `${stepText} - ${measureText}`; } else text = this.getCachedTranslation('ready'); if (this.elements.progressText) this.elements.progressText.textContent = text; if (this.elements.stepInfo) { this.elements.stepInfo.textContent = isPlaying ? `${currentStep + 1} / ${this.graphData.length || 1}` : ''; } }
    updateStaticTempoDisplay() { if (this.elements.currentTempo) { const initialBpm = this.graphData?.[0] || 120; this.elements.currentTempo.textContent = Math.round(initialBpm); } }
    toggleAdvancedControls() { if (!this.elements.advancedControlsContainer || !this.elements.toggleAdvanced) return; this.elements.advancedControlsContainer.classList.toggle('advanced-hidden'); const isHidden = this.elements.advancedControlsContainer.classList.contains('advanced-hidden'); const key = isHidden ? 'show_advanced_settings' : 'hide_advanced_settings'; const textSpan = this.elements.toggleAdvanced.querySelector('span'); if (textSpan) { textSpan.textContent = this.i18n.translate(key); textSpan.setAttribute('data-i18n', key); } }
    toCamelCase(str) { return str.replace(/-([a-z])/g, g => g[1].toUpperCase()); }
    updateTranslations() { this.updateLanguageDisplay(); document.querySelectorAll('[data-i18n]').forEach(element => { const key = element.getAttribute('data-i18n'); if (!key) return; const translation = this.i18n.translate(key); if (element.tagName === 'INPUT' && element.hasAttribute('placeholder')) { element.placeholder = translation; } else if (element.children.length === 0 || element.tagName === 'OPTION' || element.classList.contains('radio-group')) { element.textContent = translation; } }); document.querySelectorAll('[data-i18n-title]').forEach(element => { const key = element.getAttribute('data-i18n-title'); if (key) element.title = this.i18n.translate(key); }); this.elements.soundType.querySelectorAll('option').forEach(option => { option.textContent = this.i18n.translate(option.value); }); this.initializeSubdivisionModeSelect(); this.elements.subdivisionMode.value = this.rhythmicState.subdivisionMode; this.updateControlsState(); this.infoModalContent = null; }
    updateLanguageDisplay() { if (this.elements.currentLanguage) { const otherLang = this.i18n.getOtherLanguage(); this.elements.currentLanguage.textContent = otherLang.toUpperCase(); } }
    cacheStaticTranslations() {
        this.cachedTranslations = {
            ready: this.i18n.translate('ready'),
            paused: this.i18n.translate('paused'),
            resume: this.i18n.translate('resume'),
            step: this.i18n.translate('step'),
            measure: this.i18n.translate('measure'),
            program_complete_title: this.i18n.translate('program_complete_title'),
            heatmap_day_labels: this.i18n.translate('heatmap_day_labels').split(' '),
        };
    }
    getCachedTranslation(key) { return this.cachedTranslations?.[key] || this.i18n.translate(key); }
    updateProgramDescription() { if (this.elements.programDescription) { const key = `program_${this.elements.trainingProgram.value}_desc`; this.elements.programDescription.innerHTML = this.i18n.translate(key); } }
    updateAccentButtons() {
        if (!this.cachedDOMElements || !this.cachedDOMElements.accentButtons) return;
        const beats = Math.min(this.rhythmicState.currentTimeSignature.beats, this.MAX_BEATS);
        // Pass custom grouping to analyzeTimeSignature to ensure accurate default strong beats
        const timeInfo = this.metronome.analyzeTimeSignature(
            this.rhythmicState.currentTimeSignature.beats,
            this.rhythmicState.currentTimeSignature.noteValue,
            this.rhythmicState.customGroupingPattern
        );
        const defaultStrongBeats = timeInfo.strongBeats;
        this.cachedDOMElements.accentButtons.forEach((button, i) => {
            if (i < beats) {
                button.style.display = 'flex';
                button.className = 'accent-btn';
                if (this.rhythmicState.customAccents.includes(i)) {
                    button.classList.add('accented');
                } else if (defaultStrongBeats.includes(i)) {
                    button.classList.add('strong-beat-default');
                }
            } else {
                button.style.display = 'none';
            }
        });
    }

    updateBeatIndicators() {
        const container = this.elements.beatIndicators;
        if (!container) return;
        
        // BUGFIX: Reset lastActiveIndicator immediately before rebuilding DOM
        // This ensures updateBeatIndicatorDisplay won't try to un-highlight a detached element
        // and starts with a clean slate for new elements.
        this.lastActiveIndicator = null; 

        // Pass customGroupingPattern to analyzeTimeSignature
        const timeInfo = this.metronome.analyzeTimeSignature(
            this.rhythmicState.currentTimeSignature.beats,
            this.rhythmicState.currentTimeSignature.noteValue,
            this.rhythmicState.customGroupingPattern
        );
        container.innerHTML = ''; // Clear existing indicators
        container.className = `beat-indicators ${timeInfo.isCompound ? 'compound' : ''} ${timeInfo.feeling.includes('asymmetric') || timeInfo.feeling.includes('irregular') || timeInfo.feeling.includes('complex') ? 'asymmetric' : ''}`;

        let beatIndex = 0;
        timeInfo.grouping.forEach((groupSize, groupIndex) => {
            const group = document.createElement('div');
            group.className = 'beat-group';

            const groupLabel = document.createElement('div');
            groupLabel.className = 'beat-group-label';
            groupLabel.textContent = `${this.i18n.translate('group')} ${groupIndex + 1}`;
            group.appendChild(groupLabel);

            const indicatorsContainer = document.createElement('div');
            indicatorsContainer.className = 'beat-group-indicators';

            for (let i = 0; i < groupSize; i++) {
                const indicator = document.createElement('div');
                indicator.className = 'beat-indicator';
                indicator.setAttribute('data-beat', beatIndex++);
                indicatorsContainer.appendChild(indicator);
            }
            group.appendChild(indicatorsContainer);
            container.appendChild(group);
        });
    }

    updateTimeSignatureUI() { this.updateTimeSignatureInfo(); this.updateAccentButtons(); this.updateBeatIndicators(); }
    updateTimeSignatureInfo() {
        // Pass customGroupingPattern to analyzeTimeSignature
        const timeInfo = this.metronome.analyzeTimeSignature(
            this.rhythmicState.currentTimeSignature.beats,
            this.rhythmicState.currentTimeSignature.noteValue,
            this.rhythmicState.customGroupingPattern
        );
        let infoElement = document.querySelector('.time-signature-info');
        if (!infoElement) { infoElement = document.createElement('div'); infoElement.className = 'time-signature-info'; this.elements.measuresPerStep.closest('.control-group').after(infoElement); }
        const categoryText = this.i18n.translate(`${timeInfo.category}_time`);
        
        // BUGFIX: Make translation key generation robust. The engine can return root keys ('asymmetric') or full keys ('custom_grouping_feeling'). This handles both.
        const feelingKey = timeInfo.feeling.endsWith('_feeling') ? timeInfo.feeling : `${timeInfo.feeling}_feeling`;
        const feelingText = this.i18n.translate(feelingKey);

        const groupingText = timeInfo.grouping.join(', '); // NEW: Show actual grouping pattern
        infoElement.innerHTML = `<div class="time-signature-header"><strong>${timeInfo.signature}</strong> - <span class="time-signature-category">${categoryText}</span></div><div class="time-signature-details"><div class="time-signature-detail"><span class="detail-label">${this.i18n.translate('feeling')}:</span><span class="detail-value">${feelingText}</span></div><div class="time-signature-detail"><span class="detail-label">${this.i18n.translate('strong_beats')}:</span><span class="detail-value">${timeInfo.strongBeats.map(b => b + 1).join(', ')}</span></div><div class="time-signature-detail"><span class="detail-label">${this.i18n.translate('grouping_pattern')}:</span><span class="detail-value">${groupingText}</span></div></div>`;
    }

    // NEW: Function to update custom grouping UI state
    updateCustomGroupingUI() {
        if (!this.elements.customGroupingContainer) return;

        // Reset state
        this.elements.customGrouping.classList.remove('is-invalid');
        this.elements.customGrouping.value = this.rhythmicState.customGroupingPattern ? this.rhythmicState.customGroupingPattern.join(',') : '';

        // Update info text based on current pattern validity
        if (this.rhythmicState.customGroupingPattern) {
            const sum = this.rhythmicState.customGroupingPattern.reduce((acc, val) => acc + val, 0);
            const beatsPerMeasure = parseInt(this.elements.beatsPerMeasure.value);
            if (sum === beatsPerMeasure) {
                if (this.elements.customGroupingInfoText) this.elements.customGroupingInfoText.textContent = this.i18n.translate('custom_grouping_info_valid');
            } else {
                // This case should ideally not happen if handleCustomGroupingChange is always called
                // but adding for robustness.
                if (this.elements.customGroupingInfoText) this.elements.customGroupingInfoText.textContent = this.i18n.translate('custom_grouping_error_sum', { beatsPerMeasure: beatsPerMeasure, sum: sum });
                this.elements.customGrouping.classList.add('is-invalid');
            }
        } else {
            if (this.elements.customGroupingInfoText) this.elements.customGroupingInfoText.textContent = this.i18n.translate('custom_grouping_info');
        }
    }

    clearBeatIndicators() {
        if (!this.elements.beatIndicators) return;
        // BUGFIX: Ensure lastActiveIndicator is reset when clearing indicators
        if (this.lastActiveIndicator && this.elements.beatIndicators.contains(this.lastActiveIndicator)) {
            this.lastActiveIndicator.className = 'beat-indicator'; // Clean up the last active one if it exists and is still in DOM
        }
        this.lastActiveIndicator = null; // Ensure it's null after clearing
        // Also ensure any lingering active classes are removed from any remaining elements (shouldn't be any if innerHTML is cleared, but good for robustness)
        this.elements.beatIndicators.querySelectorAll('.active').forEach(el => el.classList.remove('active', 'strong', 'weak', 'subdivision'));
    }

    updateBeatIndicatorDisplay(beatData) { 
        if (!beatData) return; // Safeguard if beatData is somehow null/undefined

        // BUGFIX: Only try to remove class from last active if it still points to a valid DOM element AND is still part of the DOM
        // This is crucial because updateBeatIndicators might have rebuilt the DOM, detaching the old lastActiveIndicator.
        if (this.lastActiveIndicator && this.elements.beatIndicators.contains(this.lastActiveIndicator)) {
            this.lastActiveIndicator.className = 'beat-indicator'; 
        } else if (this.lastActiveIndicator) {
            // If lastActiveIndicator existed but is no longer in the DOM, clear the reference.
            this.lastActiveIndicator = null;
        }

        const activeIndicator = this.elements.beatIndicators.querySelector(`[data-beat="${beatData.beatInMeasure}"]`);
        if (activeIndicator) {
            let newClass = 'beat-indicator active ';
            if (beatData.isSubdivision) newClass += 'subdivision';
            else if (beatData.isStrong) newClass += 'strong';
            else newClass += 'weak';
            activeIndicator.className = newClass;
            this.lastActiveIndicator = activeIndicator; // Update to the new active element
        } else {
            // If activeIndicator is null, it means the element was not found.
            // This might happen in a very tight race condition or if beatInMeasure is unexpected.
            // Reset lastActiveIndicator to null to prevent issues on next beat, as we couldn't find the current one.
            this.lastActiveIndicator = null;
            console.warn(`Beat indicator for beat ${beatData.beatInMeasure} not found.`); // Add warning for debugging
        }
    }

    updateMetronomeDisplay(beatData) { 
        const circle = this.elements.metronomeCircle; 
        if (!circle) return; // Safeguard if circle element is null or not found

        let scale = 1.08; 
        let borderColor = 'var(--border-strong)'; 
        if (beatData.isSubdivision) { 
            scale = 1.03; 
        } else if (beatData.isStrong) { 
            scale = 1.12; 
            borderColor = 'var(--accent-primary)'; 
        } 
        
        // The animation logic here is robust. It creates a new animation instance each time.
        // The throttle and requestAnimationFrame ensure this is not called excessively fast.
        circle.animate([
            { transform: `scale(${scale})`, borderColor: borderColor }, 
            { transform: 'scale(1)', borderColor: 'var(--border-color)' }
        ], { duration: 150, easing: 'ease-out' }); 
    }

    initializeSubdivisionModeSelect() { const select = this.elements.subdivisionMode; if (!select) return; const options = [{ value: 'none', key: 'subdivision_none' }, { value: 'binary', key: 'subdivision_binary' }, { value: 'ternary', key: 'subdivision_ternary' }, { value: 'swing', key: 'subdivision_swing' }]; const currentValue = select.value; select.innerHTML = ''; options.forEach(opt => { const option = document.createElement('option'); option.value = opt.value; option.setAttribute('data-i18n', opt.key); option.textContent = this.i18n.translate(opt.key); select.appendChild(option); }); select.value = currentValue; }
    
    addCustomTempoPoint() {
        this.customTempoPoints.push(120);
        this.updateCustomTempoDisplay();
        this.handleProgramChange();
    }
    
    removeCustomTempoPoint(index) {
        if (this.customTempoPoints.length <= 1) return;
        this.customTempoPoints.splice(index, 1);
        this.updateCustomTempoDisplay();
        this.handleProgramChange();
    }
    
    updateCustomTempoDisplay() {
        if (!this.elements.customTempoPoints) return;
        this.elements.customTempoPoints.innerHTML = this.customTempoPoints.map((bpm, index) =>
            `<div class="tempo-point">
                <input type="number" class="control-input tempo-point-input" min="20" max="400" value="${bpm}" data-index="${index}" placeholder="BPM">
                <button type="button" class="tempo-point-remove" data-index="${index}" ${this.customTempoPoints.length <= 1 ? 'disabled' : ''}>×</button>
            </div>`
        ).join('');
    }

    drawGraph() { const rect = this.elements.graphContainer.getBoundingClientRect(); if (rect.width === 0 || rect.height === 0) return; const margin = { top: 30, right: 85, bottom: 50, left: 85 }; const width = rect.width - margin.left - margin.right; const height = rect.height - margin.top - margin.bottom; if (width <= 0 || height <= 0) return; const dataMin = this.graphData.length > 0 ? Math.min(...this.graphData) : 60; const dataMax = this.graphData.length > 0 ? Math.max(...this.graphData) : 180; const padding = (dataMax - dataMin) === 0 ? 20 : (dataMax - dataMin) * 0.1; const minBpm = Math.max(0, dataMin - padding); const maxBpm = dataMax + padding; const scale = { x: i => margin.left + (this.graphData.length > 1 ? (i / (this.graphData.length - 1)) * width : width / 2), y: bpm => { const range = maxBpm - minBpm; if (range === 0) return margin.top + height / 2; return margin.top + height - ((bpm - minBpm) / range) * height; } }; if (this.needsGraphRedraw.background) { this.renderBackgroundGraph(rect, margin, width, height, scale, minBpm, maxBpm); this.needsGraphRedraw.background = false; } if (this.needsGraphRedraw.curve) { this.renderCurveGraph(rect, margin, width, height, scale, dataMax); this.needsGraphRedraw.curve = false; } }
    cacheGraphColors() { const styles = getComputedStyle(document.documentElement); this.graphColors = { primaryText: styles.getPropertyValue('--primary-text').trim(), secondaryText: styles.getPropertyValue('--secondary-text').trim(), fontFamily: styles.getPropertyValue('--font-family-primary').trim() || `"Avenir Next", "Nunito Sans", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif`, ascendingColor: styles.getPropertyValue('--graph-curve-primary').trim(), descendingColor: styles.getPropertyValue('--graph-curve-secondary').trim(), pointColor: styles.getPropertyValue('--graph-point-primary').trim(), activePointColor: styles.getPropertyValue('--graph-point-active').trim() }; }
    renderBackgroundGraph(rect, margin, width, height, scale, minBpm, maxBpm) { if (!this.backgroundContext) return; const canvas = this.backgroundCanvas; const ctx = this.backgroundContext; const dpr = window.devicePixelRatio || 1; canvas.width = rect.width * dpr; canvas.height = rect.height * dpr; ctx.scale(dpr, dpr); ctx.clearRect(0, 0, rect.width, rect.height); this.drawGridAndLabels(ctx, margin, width, height, scale.x, scale.y, minBpm, maxBpm); }
    renderCurveGraph(rect, margin, width, height, scale, dataMax) { if (!this.curveContext || !this.graphData || this.graphData.length === 0) return; const canvas = this.curveCanvas; const ctx = this.curveContext; const dpr = window.devicePixelRatio || 1; canvas.width = rect.width * dpr; canvas.height = rect.height * dpr; ctx.scale(dpr, dpr); ctx.clearRect(0, 0, rect.width, rect.height); this.drawCurve(ctx, scale.x, scale.y, margin, height, dataMax); this.drawDataPoints(ctx, scale.x, scale.y, margin, width, dataMax); }
    updateGraphIndicator(currentStep) { const line = this.elements.graphIndicatorLine; const point = this.elements.graphActivePoint; if (!line || !point) return; if (currentStep < 0 || !this.uiState.isPlaying || !this.graphData[currentStep]) { line.style.display = 'none'; point.style.display = 'none'; return; } const rect = this.elements.graphContainer.getBoundingClientRect(); if (rect.width === 0) return; const margin = { top: 30, right: 85, bottom: 50, left: 85 }; const width = rect.width - margin.left - margin.right; const height = rect.height - margin.top - margin.bottom; const dataMin = Math.min(...this.graphData); const dataMax = Math.max(...this.graphData); const padding = (dataMax - dataMin) === 0 ? 20 : (dataMax - dataMin) * 0.1; const minBpm = Math.max(0, dataMin - padding); const maxBpm = dataMax + padding; const yScale = bpm => { const range = maxBpm - minBpm; if (range === 0) return margin.top + height / 2; return margin.top + height - ((bpm - minBpm) / range) * height; }; const xScale = i => margin.left + (this.graphData.length > 1 ? (i / (this.graphData.length - 1)) * width : width / 2); const x = xScale(currentStep); const y = yScale(this.graphData[currentStep]); line.style.height = `${rect.height - margin.top - margin.bottom}px`; line.style.top = `${margin.top}px`; line.style.transform = `translateX(${x}px)`; point.style.transform = `translate(${x}px, ${y}px)`; line.style.display = 'block'; point.style.display = 'block'; }
    drawGridAndLabels(ctx, margin, width, height, xScale, yScale, minBpm, maxBpm) { const { primaryText, secondaryText, fontFamily } = this.graphColors; ctx.fillStyle = secondaryText; if (this.graphData.length > 0) { const peakBpm = Math.max(...this.graphData); const peakIndex = this.graphData.indexOf(peakBpm); const leftAxisValues = new Set(this.graphData.slice(0, peakIndex + 1)); const rightAxisValues = new Set(this.graphData.slice(peakIndex + 1)); leftAxisValues.forEach(bpm => { const y = yScale(bpm); ctx.font = `600 12px ${fontFamily}`; ctx.textAlign = 'right'; ctx.textBaseline = 'middle'; ctx.fillText(Math.round(bpm), margin.left - 20, y); }); rightAxisValues.forEach(bpm => { if (!leftAxisValues.has(bpm)) { const y = yScale(bpm); ctx.font = `600 12px ${fontFamily}`; ctx.textAlign = 'left'; ctx.textBaseline = 'middle'; ctx.fillText(Math.round(bpm), margin.left + width + 20, y); } }); } const totalSteps = this.graphData.length; if (totalSteps > 1) { const xLabels = new Set([1, totalSteps]); if (totalSteps > 4) { xLabels.add(Math.round(1 + (totalSteps - 1) / 4)); xLabels.add(Math.round(1 + (totalSteps - 1) / 2)); xLabels.add(Math.round(1 + ((totalSteps - 1) * 3) / 4)); } else if (totalSteps > 2) { xLabels.add(Math.round(1 + (totalSteps - 1) / 2)); } xLabels.forEach(stepNumber => { const x = xScale(stepNumber - 1); ctx.font = `500 12px ${fontFamily}`; ctx.textAlign = 'center'; ctx.textBaseline = 'top'; ctx.fillText(stepNumber, x, margin.top + height + 15); }); } ctx.fillStyle = primaryText; ctx.font = `500 13px ${fontFamily}`; ctx.letterSpacing = '1px'; const titleText = this.i18n.translate('steps').toUpperCase(); ctx.textAlign = 'center'; ctx.textBaseline = 'top'; ctx.fillText(titleText, margin.left + width / 2, margin.top + height + 32); ctx.save(); ctx.translate(25, margin.top + height / 2); ctx.rotate(-Math.PI / 2); ctx.textAlign = 'center'; ctx.textBaseline = 'bottom'; ctx.fillText(`${this.i18n.translate('tempo')} (BPM)`.toUpperCase(), 0, 0); ctx.restore(); ctx.letterSpacing = '0px'; }
    drawCurve(ctx, xScale, yScale, margin, height, dataMax) { if (this.graphData.length < 2) return; const { ascendingColor, descendingColor } = this.graphColors; const peakIndex = this.graphData.indexOf(dataMax); const gradient = ctx.createLinearGradient(0, margin.top, 0, margin.top + height); gradient.addColorStop(0, ascendingColor + '30'); gradient.addColorStop(1, ascendingColor + '05'); ctx.fillStyle = gradient; ctx.beginPath(); ctx.moveTo(xScale(0), margin.top + height); this.graphData.forEach((bpm, i) => ctx.lineTo(xScale(i), yScale(bpm))); ctx.lineTo(xScale(this.graphData.length - 1), margin.top + height); ctx.closePath(); ctx.fill(); ctx.lineWidth = 2.5; ctx.strokeStyle = ascendingColor; ctx.beginPath(); ctx.moveTo(xScale(0), yScale(this.graphData[0])); for (let i = 1; i <= peakIndex && i < this.graphData.length; i++) { ctx.lineTo(xScale(i), yScale(this.graphData[i])); } ctx.stroke(); if (peakIndex < this.graphData.length - 1) { ctx.strokeStyle = descendingColor; ctx.beginPath(); ctx.moveTo(xScale(peakIndex), yScale(this.graphData[peakIndex])); for (let i = peakIndex + 1; i < this.graphData.length; i++) { ctx.lineTo(xScale(i), yScale(this.graphData[i])); } ctx.stroke(); } }
    drawDataPoints(ctx, xScale, yScale, margin, width, dataMax) { const { pointColor, activePointColor, ascendingColor, descendingColor } = this.graphColors; const peakIndex = this.graphData.indexOf(dataMax); const leftAxisValues = new Set(this.graphData.slice(0, peakIndex + 1)); this.graphData.forEach((bpm, index) => { const x = xScale(index); const y = yScale(bpm); let lineColor = (index <= peakIndex) ? ascendingColor : descendingColor; ctx.strokeStyle = lineColor; ctx.lineWidth = 1; ctx.setLineDash([2, 4]); ctx.beginPath(); if (leftAxisValues.has(bpm)) { ctx.moveTo(x, y); ctx.lineTo(margin.left, y); } else { ctx.moveTo(x, y); ctx.lineTo(margin.left + width, y); } if (bpm === dataMax) { ctx.moveTo(margin.left, y); ctx.lineTo(margin.left + width, y); } ctx.stroke(); ctx.setLineDash([]); ctx.fillStyle = pointColor; ctx.beginPath(); ctx.arc(x, y, 4, 0, Math.PI * 2); ctx.fill(); }); }
    handleGraphHover(event) { if (!this.graphData.length) return; const rect = this.elements.graphContainer.getBoundingClientRect(); const mouseX = event.clientX - rect.left; const margin = { left: 85, right: 85 }; const graphWidth = rect.width - margin.left - margin.right; const stepIndex = Math.round(((mouseX - margin.left) / graphWidth) * (this.graphData.length - 1)); if (stepIndex >= 0 && stepIndex < this.graphData.length) { const bpm = Math.round(this.graphData[stepIndex]); const xPos = margin.left + (this.graphData.length > 1 ? (stepIndex / (this.graphData.length - 1)) * graphWidth : graphWidth / 2); this.showGraphTooltip(xPos, event.clientY - rect.top, stepIndex + 1, bpm); } else { this.hideGraphTooltip(); } }
    
    handleGraphClick(event) {
        if (this.elements.trainingProgram.value !== 'custom' || !this.graphData.length) return;
        
        const rect = this.elements.graphContainer.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;
        const margin = { top: 30, right: 85, bottom: 50, left: 85 };
        
        const dataMin = Math.min(...this.graphData);
        const dataMax = Math.max(...this.graphData);
        const padding = (dataMax - dataMin) === 0 ? 20 : (dataMax - dataMin) * 0.1;
        const minBpm = Math.max(0, dataMin - padding);
        const maxBpm = dataMax + padding;
        
        const graphWidth = rect.width - margin.left - margin.right;
        const graphHeight = rect.height - margin.top - margin.bottom;

        const stepIndex = Math.round(((mouseX - margin.left) / graphWidth) * (this.graphData.length - 1));

        if (stepIndex >= 0 && stepIndex < this.customTempoPoints.length) {
            const range = maxBpm - minBpm;
            const newBpm = Math.round(maxBpm - ((mouseY - margin.top) / graphHeight) * range);
            
            this.customTempoPoints[stepIndex] = Math.max(20, Math.min(400, newBpm));
            
            this.updateCustomTempoDisplay();
            this.handleProgramChange();
        }
    }

    showGraphTooltip(x, y, step, bpm) { if (!this.elements.graphTooltip) return; const isCustom = this.elements.trainingProgram.value === 'custom'; const clickHint = isCustom ? `<div class="tooltip-hint">${this.i18n.translate('click_to_modify')}</div>` : ''; this.elements.graphTooltip.innerHTML = `<div class="tooltip-bpm">${bpm} ${this.i18n.translate('tempo_unit_bpm')}</div><div class="tooltip-step">${this.i18n.translate('step')} ${step}/${this.graphData.length}</div>${clickHint}`; this.elements.graphTooltip.style.left = `${x + 15}px`; this.elements.graphTooltip.style.top = `${y}px`; this.elements.graphTooltip.classList.add('show'); }
    hideGraphTooltip() { if (this.elements.graphTooltip) this.elements.graphTooltip.classList.remove('show'); }
    triggerVisualFlash(beatData) { const overlay = this.elements.visualFlashOverlay; if (!overlay) return; overlay.animate([{ opacity: 0.12, background: beatData.isStrong ? 'radial-gradient(circle, var(--accent-primary) 0%, transparent 70%)' : 'radial-gradient(circle, var(--accent-secondary) 0%, transparent 70%)' }, { opacity: 0 }], { duration: 100, easing: 'ease-out' }); }
    toggleTheme() { this.currentTheme = document.documentElement.getAttribute('data-theme') === 'dark' ? 'light' : 'dark'; document.documentElement.setAttribute('data-theme', this.currentTheme); localStorage.setItem('theme', this.currentTheme); this.cacheGraphColors(); this.needsGraphRedraw = { background: true, curve: true }; this.drawGraph(); }
    async toggleLanguage() {
        const newLang = this.i18n.getOtherLanguage();
        this.i18n.setLanguage(newLang);
        this.cacheStaticTranslations(); // Recache i18n for new language
        this.updateTranslations();
        this.updateProgramDescription(); // CORRECTION: Rappeler pour traduire la description.
        this.updateTimeSignatureInfo(); // CORRECTION: Rappeler pour traduire les infos de signature.
        this.infoModalContent = null; // Invalidate info modal cache to rebuild with new language
        this.needsGraphRedraw = { background: true, curve: true };
        this.drawGraph();
    }
    loadTheme() { const savedTheme = localStorage.getItem('theme') || 'dark'; document.documentElement.setAttribute('data-theme', savedTheme); if (this.elements.themeToggle) { const icon = this.elements.themeToggle.querySelector('.btn-icon'); if (icon) icon.textContent = savedTheme === 'dark' ? '☀️' : '🌙'; } }
    showDialog(message, title, isError = true) { this.lastFocusedElement = document.activeElement; if (this.elements.dialogModal) { this.elements.dialogTitle.textContent = title; this.elements.dialogMessage.textContent = message; this.elements.dialogModal.classList.toggle('is-error', isError); this.elements.dialogModal.classList.toggle('is-success', !isError); this.elements.dialogModal.classList.add('show'); this.elements.dialogClose.focus(); } }
    hideDialog() { if (this.elements.dialogModal) this.elements.dialogModal.classList.remove('show'); if (this.lastFocusedElement) this.lastFocusedElement.focus(); }

    showInfoModal() {
        this.lastFocusedElement = document.activeElement;
        // Invalidate content cache if language changes, to update the dynamic language icon
        if (this.infoModalLang !== this.i18n.getCurrentLanguage()) {
            this.infoModalContent = null;
            this.infoModalLang = this.i18n.getCurrentLanguage();
        }

        if (!this.infoModalContent) {
            if (this.elements.infoModalTitle) this.elements.infoModalTitle.textContent = this.i18n.translate('info_modal_title');
            
            const themeIcon = document.documentElement.getAttribute('data-theme') === 'dark' ? '☀️' : '🌙';
            const langIcon = this.i18n.getOtherLanguage().toUpperCase();
            
            // NEW ORDER: Start with intro and key features
            let contentHtml = `
                <p>${this.i18n.translate('info_modal_intro')}</p>
                <h3>${this.i18n.translate('info_modal_features_title')}</h3>
                <ul>${this.i18n.translate('info_modal_features_list')}</ul>

                <h3>${this.i18n.translate('info_modal_controls_title')}</h3>
                <ul class="controls-explanation-list">
                    <li>
                        <span class="control-icon-explainer">${themeIcon}</span>
                        <div><strong>${this.i18n.translate('theme_toggle_label')}:</strong> ${this.i18n.translate('info_modal_controls_theme_desc')}</div>
                    </li>
                    <li>
                        <span class="control-icon-explainer">ⓘ</span>
                        <div><strong>${this.i18n.translate('info_btn_label')}:</strong> ${this.i18n.translate('info_modal_controls_info_desc')}</div>
                    </li>
                    <li>
                        <span class="control-icon-explainer">💬</span>
                        <div><strong>${this.i18n.translate('feedback_btn_label')}:</strong> ${this.i18n.translate('info_modal_controls_feedback_desc')}</div>
                    </li>
                    <li>
                        <span class="control-icon-explainer lang-icon-explainer">${langIcon}</span>
                        <div><strong>${this.i18n.translate('language_toggle_label')}:</strong> ${this.i18n.translate('info_modal_controls_language_desc')}</div>
                    </li>
                </ul>
                
                <h3>${this.i18n.translate('rhythmic_theory_title')}</h3>
                <p>${this.i18n.translate('rhythmic_theory_explanation')}</p>
                <ul class="theory-list">
                    <li><strong>${this.i18n.translate('simple_time_theory_label')}:</strong> ${this.i18n.translate('simple_time_theory_desc')}</li>
                    <li><strong>${this.i18n.translate('compound_time_theory_label')}:</strong> ${this.i18n.translate('compound_time_theory_desc')}</li>
                    <li><strong>${this.i18n.translate('asymmetric_time_theory_label')}:</strong> ${this.i18n.translate('asymmetric_time_theory_desc')}</li>
                </ul>
            `;

            const programTypes = ['pyramid', 'consolidation', 'endurance', 'speed', 'agility', 'ladder', 'custom'];
            contentHtml += `<h3>${this.i18n.translate('info_modal_programs_title')}</h3>`;
            programTypes.forEach(type => {
                contentHtml += `<div class="program-detail"><h4>${this.i18n.translate(`program_${type}`)}</h4><p>${this.i18n.translate(`info_${type}_desc`)}</p></div>`;
            });
            
            this.infoModalContent = contentHtml;
        }

        if (this.elements.infoModalBody) this.elements.infoModalBody.innerHTML = this.infoModalContent;
        if (this.elements.infoModal) {
            this.elements.infoModal.classList.add('show');
            this.elements.infoModalClose.focus();
        }
    }

    hideInfoModal() { if (this.elements.infoModal) this.elements.infoModal.classList.remove('show'); if (this.lastFocusedElement) this.lastFocusedElement.focus(); }

    showFeedbackModal() {
        this.lastFocusedElement = document.activeElement;
        if (this.elements.feedbackModal) {
            this.elements.feedbackModal.classList.add('show');
            // Reset form on open
            this.elements.feedbackForm.reset();
            const formFeedback = this.elements.feedbackFormMessage;
            if (formFeedback) {
                formFeedback.style.display = 'none';
                formFeedback.className = 'form-feedback'; // Reset class
            }
            this.elements.feedbackDescription.focus();
        }
    }

    hideFeedbackModal() {
        if (this.elements.feedbackModal) {
            this.elements.feedbackModal.classList.remove('show');
        }
        if (this.lastFocusedElement) {
            this.lastFocusedElement.focus();
        }
    }

    async handleFeedbackSubmit(event) {
        event.preventDefault();
        const form = this.elements.feedbackForm;
        const submitBtn = this.elements.feedbackSubmitBtn;
        const feedbackMsg = this.elements.feedbackFormMessage;

        submitBtn.disabled = true;
        submitBtn.innerHTML = `<span data-i18n="feedback_sending">${this.i18n.translate('feedback_sending')}</span>`;
        if (feedbackMsg) {
            feedbackMsg.style.display = 'none';
            feedbackMsg.className = 'form-feedback';
        }

        // --- Validation ---
        const type = form.querySelector('input[name="feedback-type"]:checked')?.value;
        const description = this.elements.feedbackDescription.value.trim();
        
        if (!type) {
            this.showFormError(this.i18n.translate('feedback_validation_type'));
            submitBtn.disabled = false;
            submitBtn.innerHTML = `<span>${this.i18n.translate('feedback_submit')}</span>`;
            return;
        }
        if (description.length < 10) {
            this.showFormError(this.i18n.translate('feedback_validation_description'));
            submitBtn.disabled = false;
            submitBtn.innerHTML = `<span>${this.i18n.translate('feedback_submit')}</span>`;
            return;
        }

        // --- Submission ---
        // BUGFIX: Create a plain JavaScript object to send as JSON
        const dataToSend = {
            'feedback-type': type,
            description: description,
            application: 'SpeedCurve Metronome v2.7',
            language: this.i18n.getCurrentLanguage(),
            browser: navigator.userAgent,
            url: window.location.href,
            // BUGFIX: Add a _replyto field to improve email deliverability and avoid spam filters.
            _replyto: "cyberpunkredcompagnon+speedcurvemetronome@gmail.com", // Use a placeholder as there's no email field
        };

        const FORMSPREE_ENDPOINT = 'https://formspree.io/f/meozgbdb';

        try {
            // BUGFIX: Change fetch request to send JSON
            const response = await fetch(FORMSPREE_ENDPOINT, {
                method: 'POST',
                body: JSON.stringify(dataToSend),
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                let errorMessage = 'Formspree submission failed';
                try {
                    const data = await response.json();
                    if (data && data.errors) {
                        errorMessage = data.errors.map(e => e.message).join(', ');
                    }
                } catch (e) {
                    // Ignore JSON parsing error if response is not JSON
                }
                throw new Error(errorMessage);
            }

            this.hideFeedbackModal();
            this.showDialog(
                this.i18n.translate('feedback_success_message'),
                this.i18n.translate('feedback_success_title'),
                false // isError = false
            );

        } catch (error) {
            console.error('Feedback submission error:', error);
            this.showFormError(`${this.i18n.translate('feedback_error_generic')} (${error.message})`);
        } finally {
            if (!this.elements.feedbackSubmitBtn) return;
            submitBtn.disabled = false;
            submitBtn.innerHTML = `<span>${this.i18n.translate('feedback_submit')}</span>`;
        }
    }

    showFormError(message) {
        const feedbackMsg = this.elements.feedbackFormMessage;
        const submitBtn = this.elements.feedbackSubmitBtn;
        if (!feedbackMsg || !submitBtn) return;

        feedbackMsg.textContent = message;
        feedbackMsg.className = 'form-feedback error'; // Set class for styling
        feedbackMsg.style.display = 'block';

        submitBtn.disabled = false;
        submitBtn.innerHTML = `<span>${this.i18n.translate('feedback_submit')}</span>`;
    }

    showLoadingScreen() { document.getElementById('loading-screen')?.classList.remove('hidden'); }
    hideLoadingScreen() { document.getElementById('loading-screen')?.classList.add('hidden'); }
}

document.addEventListener('DOMContentLoaded', async () => {
    try {
        const app = new SpeedCurveMetronomeApp();
        await app.initialize();
        window.speedCurveApp = app;
    } catch (error) {
        console.error('Application initialization failed:', error);
        document.body.innerHTML = `<div style="color:red;padding:20px;"><h2>Application Error</h2><p>${error.message}</p></div>`;
    }
});